import styled, { css, type DefaultTheme } from 'styled-components';
import { ButtonGroup, ButtonVariants, ButtonWrapper } from '../../Button';
import { Box } from '../../Core';

const getSelectedVariantStateStyles = ({
  background,
  color,
  backgroundHover,
  colorHover,
  backgroundFocus,
  colorFocus,
  backgroundActive,
  colorActive,
}: {
  background: string;
  color: string;
  backgroundHover: string;
  colorHover: string;
  backgroundFocus: string;
  colorFocus: string;
  backgroundActive: string;
  colorActive: string;
}) => css`
  & {
    background: ${background};
    color: ${color};
  }

  &:hover {
    background: ${backgroundHover};
    color: ${colorHover};
  }

  &:focus,
  &:focus-visible {
    background: ${backgroundFocus};
    color: ${colorFocus};
  }

  &:active {
    background: ${backgroundActive};
    color: ${colorActive};
  }
`;

export const ToggleGroupRoot = styled(ButtonGroup)`
  position: relative;
  overflow: hidden;

  ${ButtonWrapper} {
    display: flex;
  }

  /* Use ButtonWrapper-scoped selectors to beat base button state styles without !important. */
  ${ButtonWrapper}[data-toggle-transparent='true'] {
    box-shadow: none;
    border-color: transparent;
    background: transparent;
    color: ${({ theme }) => theme.colorTextMutedButton};

    &:hover,
    &:focus {
      box-shadow: ${({ theme }) => theme.boxShadowDefaultButtonHover};
      border-color: ${({ theme }) => theme.borderColorDefaultButtonHover};
      background: ${({ theme }) => theme.backgroundDefaultButtonHover};
      color: ${({ theme }) => theme.colorTextDefaultButtonHover};
    }
  }

  ${ButtonWrapper}[data-toggle-animated='true'][data-selected='true'] {
    background: transparent;

    &:hover,
    &:focus,
    &:focus-visible,
    &:active {
      background: transparent;
    }
  }

  [data-toggle-default='false'][data-toggle-animated='true'][data-selected='true'] {
    &[data-toggle-variant='Default'] {
      color: ${({ theme }) => theme.colorTextDefaultButton};
    }

    &[data-toggle-variant='Primary'] {
      color: ${({ theme }) => theme.colorTextPrimaryButton};
    }

    &[data-toggle-variant='Positive'] {
      color: ${({ theme }) => theme.colorTextPositiveButton};
    }

    &[data-toggle-variant='Negative'] {
      color: ${({ theme }) => theme.colorTextNegativeButton};
    }

    &[data-toggle-variant='Warning'] {
      color: ${({ theme }) => theme.colorTextWarningButton};
    }

    &[data-toggle-variant='Priority'] {
      color: ${({ theme }) => theme.colorTextPriorityButton};
    }

    &[data-toggle-variant='Muted'] {
      color: ${({ theme }) => theme.colorTextMutedButton};
    }
  }

  [data-toggle-default='false'][data-selected='true']:not([data-toggle-animated='true']) {
    &[data-toggle-variant='Default'] {
      ${({ theme }) =>
        getSelectedVariantStateStyles({
          background: theme.backgroundDefaultButton,
          color: theme.colorTextDefaultButton,
          backgroundHover: theme.backgroundDefaultButtonHover,
          colorHover: theme.colorTextDefaultButtonHover,
          backgroundFocus: theme.backgroundDefaultButtonFocus,
          colorFocus: theme.colorTextDefaultButtonFocus,
          backgroundActive: theme.backgroundDefaultButtonActive,
          colorActive: theme.colorTextDefaultButtonActive,
        })}
    }

    &[data-toggle-variant='Primary'] {
      ${({ theme }) =>
        getSelectedVariantStateStyles({
          background: theme.backgroundPrimaryButton,
          color: theme.colorTextPrimaryButton,
          backgroundHover: theme.backgroundPrimaryButtonHover,
          colorHover: theme.colorTextPrimaryButtonHover,
          backgroundFocus: theme.backgroundPrimaryButtonFocus,
          colorFocus: theme.colorTextPrimaryButtonFocus,
          backgroundActive: theme.backgroundPrimaryButtonActive,
          colorActive: theme.colorTextPrimaryButtonActive,
        })}
    }

    &[data-toggle-variant='Positive'] {
      ${({ theme }) =>
        getSelectedVariantStateStyles({
          background: theme.backgroundPositiveButton,
          color: theme.colorTextPositiveButton,
          backgroundHover: theme.backgroundPositiveButtonHover,
          colorHover: theme.colorTextPositiveButtonHover,
          backgroundFocus: theme.backgroundPositiveButtonFocus,
          colorFocus: theme.colorTextPositiveButtonFocus,
          backgroundActive: theme.backgroundPositiveButtonActive,
          colorActive: theme.colorTextPositiveButtonActive,
        })}
    }

    &[data-toggle-variant='Negative'] {
      ${({ theme }) =>
        getSelectedVariantStateStyles({
          background: theme.backgroundNegativeButton,
          color: theme.colorTextNegativeButton,
          backgroundHover: theme.backgroundNegativeButtonHover,
          colorHover: theme.colorTextNegativeButtonHover,
          backgroundFocus: theme.backgroundNegativeButtonFocus,
          colorFocus: theme.colorTextNegativeButtonFocus,
          backgroundActive: theme.backgroundNegativeButtonActive,
          colorActive: theme.colorTextNegativeButtonActive,
        })}
    }

    &[data-toggle-variant='Warning'] {
      ${({ theme }) =>
        getSelectedVariantStateStyles({
          background: theme.backgroundWarningButton,
          color: theme.colorTextWarningButton,
          backgroundHover: theme.backgroundWarningButtonHover,
          colorHover: theme.colorTextWarningButtonHover,
          backgroundFocus: theme.backgroundWarningButtonFocus,
          colorFocus: theme.colorTextWarningButtonFocus,
          backgroundActive: theme.backgroundWarningButtonActive,
          colorActive: theme.colorTextWarningButtonActive,
        })}
    }
  }

  [data-toggle-default='true'] {
    box-shadow: none;
    border-color: transparent;
    background: transparent;
    color: ${({ theme }) => theme.colors.gray['090']};
  }

  [data-toggle-default='true'][data-selected='true'] {
    background: ${({ theme }) => theme.backgroundToggleSwitchButton};
    color: ${({ theme }) => theme.colors.gray['100']};
  }

  ${ButtonWrapper}[data-toggle-default='true']:hover,
  ${ButtonWrapper}[data-toggle-default='true']:focus {
    background: ${({ theme }) => theme.backgroundToggleSwitchButtonActive};
    color: ${({ theme }) => theme.colors.gray['100']};
  }

  ${ButtonWrapper}[data-toggle-animated='true'][data-selected='false']:hover,
  ${ButtonWrapper}[data-toggle-animated='true'][data-selected='false']:focus,
  ${ButtonWrapper}[data-toggle-animated='true'][data-selected='false']:focus-visible {
    ${({ theme }) =>
      theme.type === 'light' &&
      css`
        background: ${theme.backgroundToggleSwitchButtonActive};
        transition: background 200ms ease;
      `}
  }

  [data-toggle-default='true'][disabled] {
    background: ${({ theme }) => (theme.type === 'dark' ? theme.backgroundToggleSwitchButtonActive : 'transparent')};
    color: ${({ theme }) => (theme.type === 'dark' ? theme.colors.gray['080'] : theme.colors.gray['070'])};
  }
`;

const getSelectionIndicatorBackground = (theme: DefaultTheme, variant: ButtonVariants) => {
  switch (variant) {
    case ButtonVariants.Primary:
      return theme.backgroundPrimaryButton;
    case ButtonVariants.Positive:
      return theme.backgroundPositiveButton;
    case ButtonVariants.Negative:
      return theme.backgroundNegativeButton;
    case ButtonVariants.Warning:
      return theme.backgroundWarningButton;
    case ButtonVariants.Priority:
      // backgroundPriorityButton is a dark wash in light theme, which would make the selected pill
      // look darker than its track instead of raised -- use backgroundToggleSwitchButton there instead.
      // This checks theme.type, not the token itself, so brands that already repoint
      // backgroundPriorityButton to something lighter (e.g. LightOneLiquidityTheme) get it too.
      return theme.type === 'light' ? theme.backgroundToggleSwitchButton : theme.backgroundPriorityButton;
    case ButtonVariants.Muted:
      return theme.backgroundToggleSwitchButtonActive;
    case ButtonVariants.Default:
    default:
      return theme.backgroundToggleSwitchButton;
  }
};

type SelectionIndicatorProps = {
  $variant: ButtonVariants;
  $x: number;
  $y: number;
  $width: number;
  $height: number;
};

export const SelectionIndicator = styled(Box).attrs<SelectionIndicatorProps>(({ $x, $y, $width, $height }) => ({
  style: {
    transform: `translate(${$x}px, ${$y}px)`,
    width: `${$width}px`,
    height: `${$height}px`,
  },
}))<SelectionIndicatorProps>`
  ${({ theme, $variant }) => {
    const background = getSelectionIndicatorBackground(theme, $variant);
    return css`
      background: ${background};
    `;
  }}
`;
